import type { StepId } from "./flow";
import { handoffParams, type Handoff } from "./handoff";
import type { ResultLanding } from "./submit";

/** The first step's canonical path is `/start`, not `/start/start`. */
export function stepPath(step: StepId, eventId?: string | null): string {
  return withEvent(
    step === "start" ? "/start" : `/start/${step}`,
    eventId ?? null,
  );
}

function withEvent(path: string, eventId: string | null): string {
  if (!eventId) return path;
  return `${path}?${new URLSearchParams({ event_id: eventId }).toString()}`;
}

/** The quote a submission opened. Its record's event id rides along so page
 * views keep the identifier the warehouse joins on. */
export function quotePath(quoteId: string, eventId: string | null): string {
  return withEvent(`/quotes/${encodeURIComponent(quoteId)}`, eventId);
}

/** Where a settled submission's quote opens: the quote it landed on, else
 * its case page, whose own button finds the quote. Null when it has
 * neither. */
export function landingQuotePath(landing: ResultLanding | null): string | null {
  if (landing?.quoteId) return quotePath(landing.quoteId, landing.eventId);
  if (landing?.kind === "case" && landing.caseId) {
    return withEvent(
      `/cases/${encodeURIComponent(landing.caseId)}`,
      landing.eventId,
    );
  }
  return null;
}

/** Where a signed-in visitor lands to have the submission confirmed. */
export function resumePath(submissionId: string): string {
  return `/start/resume?${new URLSearchParams({ s: submissionId }).toString()}`;
}

/** The code screen for a submission, opened straight from the lead form, or
 * from the signed link that carries `handoff`. */
export function verifyPathFor(
  submissionId: string,
  handoff: Handoff | null = null,
): string {
  const params = new URLSearchParams({ s: submissionId });
  if (handoff !== null) {
    for (const [key, value] of handoffParams(handoff)) params.set(key, value);
  }
  return `/start/verify?${params.toString()}`;
}

/** The event id the visitor's post-submit URLs carry: the response's own,
 * else the first record that has one. */
export function eventIdOf(response: {
  next: { event_id: string | null };
  records: { event_id: string | null }[];
}): string | null {
  return (
    response.next.event_id ??
    response.records.find((record) => record.event_id !== null)?.event_id ??
    null
  );
}

/** The step a history entry was pushed from, when the flow pushed it. */
export function cameFromStep(state: unknown): string | null {
  if (typeof state !== "object" || state === null) return null;
  const from = (state as Record<string, unknown>).fromStep;
  return typeof from === "string" ? from : null;
}
